import { motion } from 'framer-motion';
import { differenceInCalendarDays, format } from 'date-fns';
import { ArrowUpRight, CalendarClock, MapPin, Pencil, User, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  EpcProject,
  EpcProjectHealth as EpcProjectHealthState,
  EpcProjectMembershipRow,
} from '../../../../services/epcProjectService';
import { EPC_THEME_STYLES, SOVEREIGN, getEpcVisualTheme } from '../../utils/epcProjectVisual';
import DepartmentTagStrip from './DepartmentTagStrip';
import { EpcProjectMiniIcon } from './EpcProjectIllustration';
import { deriveProjectProgress } from './epcProjectProgress';

/**
 * Health palette — one row per status. `ink` is the saturated brand colour
 * used for text/dot. `dot` is the status indicator colour. `bg` is the chip
 * background wash. `rail` is kept for backward compat but the top rail now
 * uses the category accent (see Step 4 below).
 */
const HEALTH_TINT: Record<
  EpcProjectHealthState,
  { bg: string; border: string; dot: string; ink: string; label: string; chip: string }
> = {
  on_track:       { bg: 'rgba(31,90,71,0.12)',    border: 'rgba(31,90,71,0.20)',   dot: '#339274', ink: '#1F5A47', label: 'On track',  chip: 'rgba(31,90,71,0.12)' },
  at_risk:        { bg: 'rgba(176,123,9,0.12)',   border: 'rgba(176,123,9,0.22)',  dot: '#C9A961', ink: '#8A5A08', label: 'At risk',   chip: 'rgba(176,123,9,0.12)' },
  delayed:        { bg: 'rgba(159,18,57,0.10)',   border: 'rgba(159,18,57,0.20)',  dot: '#B91C1C', ink: '#9F1239', label: 'Delayed',   chip: 'rgba(159,18,57,0.10)' },
  completed:      { bg: 'rgba(11,59,46,0.10)',    border: 'rgba(11,59,46,0.18)',   dot: '#1F5A47', ink: '#0B3B2E', label: 'Completed', chip: 'rgba(11,59,46,0.10)' },
  upcoming:       { bg: 'rgba(90,98,117,0.10)',   border: 'rgba(90,98,117,0.16)', dot: '#94A3B8', ink: '#5A6275', label: 'Upcoming',  chip: 'rgba(90,98,117,0.10)' },
  not_configured: { bg: 'rgba(90,98,117,0.08)',   border: 'rgba(90,98,117,0.12)', dot: '#94A3B8', ink: '#5A6275', label: 'Planning',  chip: 'rgba(90,98,117,0.08)' },
};

export interface EpcProjectCardProps {
  project: EpcProject;
  canEdit: boolean;
  myMembership?: EpcProjectMembershipRow | null;
}

function refName(ref: unknown): string {
  if (ref && typeof ref === 'object' && 'name' in ref) {
    return String((ref as { name?: string }).name ?? '');
  }
  return '';
}

interface ResolvedLocations {
  primary: string;
  extra: number;
}

function resolveLocations(project: EpcProject): ResolvedLocations | null {
  const locs = Array.isArray(project.locationIds) ? project.locationIds : [];
  const names = locs.map((loc) => refName(loc)).filter((n) => n.length > 0);
  if (names.length > 1) return { primary: names[0], extra: names.length - 1 };
  if (names.length === 1) return { primary: names[0], extra: 0 };
  const single = refName(project.locationId);
  return single ? { primary: single, extra: 0 } : null;
}

function capacityFormatted(project: EpcProject): { value: string; unit: string; tooltip: string } | null {
  const ac = typeof project.acCapacityMw === 'number' && project.acCapacityMw > 0 ? project.acCapacityMw : null;
  const dc = typeof project.dcCapacityMw === 'number' && project.dcCapacityMw > 0 ? project.dcCapacityMw : null;
  const primary = ac ?? dc;
  if (primary == null) return null;
  const decimals = primary >= 100 ? 0 : primary >= 10 ? 1 : 2;
  const value = primary.toFixed(decimals);
  const unit = 'MW';
  const tooltip = ac != null && dc != null ? `AC ${ac} MW · DC ${dc} MW` : `${ac != null ? 'AC' : 'DC'} ${primary} MW`;
  return { value, unit, tooltip };
}

/**
 * Pick the most-relevant date to count down to — commissioning if set,
 * otherwise expected/planned end. Returns null when nothing useful exists.
 */
function pickCountdown(
  project: EpcProject,
  derived: ReturnType<typeof deriveProjectProgress>,
): { date: Date; label: string; deltaDays: number } | null {
  const now = new Date();
  const candidates: Array<{ date: Date | null; label: string }> = [
    {
      date: project.commissioningDate ? new Date(project.commissioningDate) : null,
      label: 'to commissioning',
    },
    { date: derived.expectedEndDate ?? null, label: 'to end (expected)' },
    { date: derived.endDate, label: 'to end (planned)' },
  ];
  for (const c of candidates) {
    if (!c.date || Number.isNaN(c.date.getTime())) continue;
    const days = differenceInCalendarDays(c.date, now);
    return { date: c.date, label: c.label, deltaDays: days };
  }
  return null;
}

const ENTRY_TRANSITION = { duration: 0.32, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] };

export default function EpcProjectCard({ project, canEdit, myMembership }: EpcProjectCardProps) {
  const theme = getEpcVisualTheme(project);
  const styles = EPC_THEME_STYLES[theme];
  const derived = deriveProjectProgress({ project });

  const typeLabel = refName(project.projectTypeId);
  const customer = refName(project.customerId);
  const location = resolveLocations(project);

  const tint = HEALTH_TINT[derived.health];
  const capacity = capacityFormatted(project);
  const plannedPct = Math.round(derived.plannedPct);
  const actualPct = derived.actualPct == null ? null : Math.round(derived.actualPct);
  const completionPct = actualPct ?? plannedPct;
  const countdown = pickCountdown(project, derived);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={ENTRY_TRANSITION}
      className="group/card relative isolate flex flex-col overflow-hidden rounded-2xl border p-5 transition-all duration-300 ease-out hover:-translate-y-1"
      style={{
        fontFeatureSettings: '"tnum", "ss01"',
        background: SOVEREIGN.cardBg,
        borderColor: SOVEREIGN.cardBorder,
        boxShadow: SOVEREIGN.cardShadow,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          '0 24px 52px -20px rgba(42,31,23,0.52), 0 4px 12px -4px rgba(11,59,46,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = SOVEREIGN.cardShadow;
      }}
    >
      {/* Banknote inner dashed rule */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 7,
          borderRadius: 11,
          border: `1px dashed ${SOVEREIGN.innerRule}`,
          pointerEvents: 'none',
        }}
      />

      {/* Theme accent stripe along top edge — uses CATEGORY accent */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[3px]"
        style={{ background: `linear-gradient(90deg, ${styles.accent}, ${styles.accent}66 60%, transparent)` }}
      />

      {/* Card content sits above the inner rule overlay */}
      <div className="relative z-10 flex flex-col flex-1">
        {/* ═════ HEADER ROW — icon · code+type · status dot ═════ */}
        <header className="flex items-start gap-3">
          <span
            className="relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl"
            style={{
              background: styles.accentSoft,
              border: `1px solid ${styles.accentSoft}`,
              color: styles.accent,
            }}
          >
            <EpcProjectMiniIcon variant={theme} accent={styles.accent} className="relative h-7 w-7" />
          </span>

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex items-center gap-1.5">
              <span
                className="font-mono text-[10.5px] font-bold tracking-[0.16em] tabular-nums uppercase"
                style={{ color: SOVEREIGN.ink }}
              >
                {project.code || '—'}
              </span>
              {(typeLabel || styles.label) && (
                <>
                  <span aria-hidden className="h-1 w-1 rounded-full bg-slate-300" />
                  <span
                    className="text-[9.5px] font-semibold uppercase tracking-[0.16em] truncate px-1 py-px rounded"
                    style={{ background: styles.accentSoft, color: styles.accent }}
                  >
                    {typeLabel || styles.label}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Status pill — compact, dot + label, tooltip carries the reason */}
          <span
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.10em]"
            title={derived.reasonText || tint.label}
            style={{
              color: tint.ink,
              backgroundColor: tint.chip,
              boxShadow: `inset 0 0 0 1px ${tint.dot}33`,
            }}
          >
            <span
              aria-hidden
              className={`h-1.5 w-1.5 rounded-full ${derived.health === 'at_risk' || derived.health === 'delayed' ? 'animate-pulse' : ''}`}
              style={{ backgroundColor: tint.dot, boxShadow: `0 0 6px ${tint.dot}66` }}
            />
            {tint.label}
          </span>
        </header>

        {/* ═════ TITLE ═════ */}
        <h3
          className="mt-3 text-[15px] font-extrabold leading-[1.3] tracking-[-0.005em] line-clamp-2 min-h-[40px]"
          title={project.name}
          style={{ color: SOVEREIGN.title }}
        >
          {project.name}
        </h3>
        <DepartmentTagStrip myMembership={myMembership ?? undefined} />

        {/* ═════ META — location · customer ═════ */}
        {(location || customer) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11.5px]" style={{ color: SOVEREIGN.ink }}>
            {location && (
              <span className="inline-flex items-center gap-1 min-w-0 max-w-[60%]">
                <MapPin className="h-3 w-3 flex-shrink-0 text-slate-400" strokeWidth={2.2} />
                <span className="truncate">
                  {location.primary}
                  {location.extra > 0 ? <span className="ml-1 text-slate-400">+{location.extra}</span> : null}
                </span>
              </span>
            )}
            {location && customer && <span aria-hidden className="h-1 w-1 rounded-full bg-slate-300" />}
            {customer && (
              <span className="inline-flex items-center gap-1 min-w-0 max-w-[55%]">
                <User className="h-3 w-3 flex-shrink-0 text-slate-400" strokeWidth={2.2} />
                <span className="truncate">{customer}</span>
              </span>
            )}
          </div>
        )}

        {/* ═════ KPI STRIP — capacity · completion · countdown ═════ */}
        <div
          className="mt-4 grid grid-cols-3 gap-2 rounded-xl border p-2"
          style={{
            background: SOVEREIGN.inputBg,
            borderColor: SOVEREIGN.barTrackBorder,
          }}
        >
          {/* Capacity */}
          <div className="flex flex-col items-start gap-0.5 px-1 min-w-0">
            <span
              className="inline-flex items-center gap-0.5 text-[8.5px] font-bold uppercase tracking-[0.12em]"
              style={{ color: SOVEREIGN.kpiCaption }}
              title={capacity?.tooltip}
            >
              <Zap className="h-2.5 w-2.5" strokeWidth={2.4} />
              Capacity
            </span>
            {capacity ? (
              <span className="text-[15px] font-extrabold leading-none tabular-nums" style={{ color: SOVEREIGN.title }}>
                {capacity.value}
                <span className="ml-0.5 text-[9.5px] font-semibold tracking-wide" style={{ color: SOVEREIGN.kpiCaption }}>
                  {capacity.unit}
                </span>
              </span>
            ) : (
              <span className="text-[13px] font-semibold text-slate-300">—</span>
            )}
          </div>

          {/* Completion */}
          <div className="flex flex-col items-start gap-0.5 px-1 border-l min-w-0" style={{ borderColor: SOVEREIGN.barTrackBorder }}>
            <span className="text-[8.5px] font-bold uppercase tracking-[0.12em]" style={{ color: SOVEREIGN.kpiCaption }}>
              Complete
            </span>
            <span className="text-[15px] font-extrabold leading-none tabular-nums" style={{ color: tint.ink }}>
              {completionPct}
              <span className="ml-0.5 text-[9.5px] font-semibold tracking-wide opacity-70">%</span>
              {actualPct == null && (
                <span className="ml-1 text-[8.5px] font-medium normal-case tracking-normal" style={{ color: SOVEREIGN.kpiCaption }}>
                  planned
                </span>
              )}
            </span>
          </div>

          {/* Countdown */}
          <div className="flex flex-col items-start gap-0.5 px-1 border-l min-w-0" style={{ borderColor: SOVEREIGN.barTrackBorder }}>
            <span className="inline-flex items-center gap-0.5 text-[8.5px] font-bold uppercase tracking-[0.12em]" style={{ color: SOVEREIGN.kpiCaption }}>
              <CalendarClock className="h-2.5 w-2.5" strokeWidth={2.4} />
              Timeline
            </span>
            {countdown ? (
              <span
                className="text-[15px] font-extrabold leading-none tabular-nums whitespace-nowrap"
                style={{ color: countdown.deltaDays < 0 ? '#9F1239' : SOVEREIGN.title }}
                title={`${format(countdown.date, 'MMM d, yyyy')} · ${countdown.label}`}
              >
                {countdown.deltaDays === 0 ? (
                  'Today'
                ) : countdown.deltaDays > 0 ? (
                  <>
                    {countdown.deltaDays}
                    <span className="ml-0.5 text-[9.5px] font-semibold tracking-wide" style={{ color: SOVEREIGN.kpiCaption }}>d</span>
                  </>
                ) : (
                  <>
                    +{Math.abs(countdown.deltaDays)}
                    <span className="ml-0.5 text-[9.5px] font-semibold tracking-wide" style={{ color: '#9F1239' }}>d late</span>
                  </>
                )}
              </span>
            ) : (
              <span className="text-[13px] font-semibold text-slate-300">—</span>
            )}
          </div>
        </div>

        {/* ═════ PROGRESS — plan vs actual (two labeled horizontal bars) ═════ */}
        <div className="mt-3.5 space-y-1.5">
          {/* Planned row */}
          <div className="flex items-center gap-2">
            <span
              className="w-[46px] shrink-0 text-right text-[8px] font-bold uppercase tracking-[0.09em]"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              Plan
            </span>
            <div
              className="relative flex-1 h-[5px] overflow-hidden rounded-full"
              style={{ background: SOVEREIGN.barTrack }}
            >
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                style={{ width: `${plannedPct}%`, background: SOVEREIGN.barPlanned }}
              />
            </div>
            <span
              className="w-8 shrink-0 text-right text-[9px] font-extrabold tabular-nums"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              {plannedPct}%
            </span>
          </div>

          {/* Actual row */}
          <div className="flex items-center gap-2">
            <span
              className="w-[46px] shrink-0 text-right text-[8px] font-bold uppercase tracking-[0.09em]"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              Actual
            </span>
            <div
              className="relative flex-1 h-[5px] overflow-hidden rounded-full"
              style={{ background: SOVEREIGN.barTrack }}
            >
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                style={{
                  width: `${actualPct ?? 0}%`,
                  background: actualPct != null && actualPct > 0 ? SOVEREIGN.barFill : 'transparent',
                }}
              />
            </div>
            <span
              className="w-8 shrink-0 text-right text-[9px] font-extrabold tabular-nums"
              style={{ color: actualPct != null ? tint.ink : '#d1d5db' }}
            >
              {actualPct != null ? `${actualPct}%` : '—'}
            </span>
          </div>
        </div>

        {/* ═════ ACTIONS ═════ */}
        <div className="mt-4 flex items-center gap-2">
          {canEdit && (
            <Link
              to={`/epc/projects/${project._id}/edit`}
              title="Edit project"
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg transition-all duration-200"
              style={{
                background: SOVEREIGN.inputBg,
                border: `1px solid ${SOVEREIGN.inputBorder}`,
                color: SOVEREIGN.title,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = SOVEREIGN.inputHoverBorder;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = SOVEREIGN.inputBorder;
              }}
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={2.4} />
            </Link>
          )}
          <Link
            to={`/epc/projects/${project._id}`}
            className="group/btn flex-1 inline-flex h-9 items-center justify-between gap-2 rounded-lg px-3 text-[12px] font-semibold tracking-tight text-white transition-all duration-200"
            style={{
              background: SOVEREIGN.btn,
              border: `1px solid ${SOVEREIGN.btnBorder}`,
              boxShadow: SOVEREIGN.btnShadow,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = SOVEREIGN.btnHover;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = SOVEREIGN.btn;
            }}
          >
            <span>Open workspace</span>
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-white/15 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5">
              <ArrowUpRight className="h-3 w-3" strokeWidth={2.6} />
            </span>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
